/**
 * Global PWA chrome: Install banner + Update toast.
 * Mount once at app root (landing + in-app).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  dismissInstallPrompt,
  isIosSafari,
  isStandaloneDisplay,
  wasInstallDismissed,
} from '../utils/pwa';
import { applyServiceWorkerUpdate } from '../registerServiceWorker';

export default function PwaChrome({ variant = 'app' }) {
  const [deferred, setDeferred] = useState(null);
  const deferredRef = useRef(null);
  const [showInstall, setShowInstall] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => {
    deferredRef.current = deferred;
  }, [deferred]);

  useEffect(() => {
    if (isStandaloneDisplay()) return undefined;

    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener('beforeinstallprompt', onBip);

    const onInstalled = () => {
      setDeferred(null);
      setShowInstall(false);
    };
    window.addEventListener('appinstalled', onInstalled);

    const onRequest = async () => {
      if (isStandaloneDisplay()) return;
      const promptEvent = deferredRef.current;
      if (promptEvent) {
        try {
          promptEvent.prompt();
          await promptEvent.userChoice;
          setDeferred(null);
          setShowInstall(false);
        } catch {
          setShowIosHelp(true);
        }
        return;
      }
      setShowInstall(true);
      setShowIosHelp(true);
    };
    window.addEventListener('spenderly:request-install', onRequest);

    const t = setTimeout(() => {
      if (wasInstallDismissed() || isStandaloneDisplay()) return;
      setShowInstall(true);
    }, variant === 'landing' ? 2200 : 4000);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
      window.removeEventListener('spenderly:request-install', onRequest);
      clearTimeout(t);
    };
  }, [variant]);

  useEffect(() => {
    const onUpdate = () => setUpdateReady(true);
    window.addEventListener('spenderly:sw-update', onUpdate);
    return () => window.removeEventListener('spenderly:sw-update', onUpdate);
  }, []);

  const closeInstall = useCallback(() => {
    dismissInstallPrompt();
    setShowInstall(false);
    setShowIosHelp(false);
  }, []);

  const onInstallClick = useCallback(async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const choice = await deferred.userChoice;
        setDeferred(null);
        if (choice?.outcome === 'accepted') setShowInstall(false);
        else closeInstall();
      } catch {
        closeInstall();
      }
      return;
    }
    setShowIosHelp(true);
  }, [deferred, closeInstall]);

  return (
    <>
      {updateReady && (
        <div className="pwa-update-bar" role="status">
          <span>A new Spenderly is ready</span>
          <button
            type="button"
            className="sp-btn-primary pwa-bar-btn"
            onClick={() => applyServiceWorkerUpdate()}
          >
            Update now
          </button>
          <button
            type="button"
            className="sp-btn-ghost pwa-bar-dismiss"
            aria-label="Dismiss update"
            onClick={() => setUpdateReady(false)}
          >
            Later
          </button>
        </div>
      )}

      {showInstall && !isStandaloneDisplay() && !updateReady && (
        <div className={`pwa-install-bar pwa-install-bar--${variant}`} role="region" aria-label="Install Spenderly">
          <div className="pwa-install-copy">
            <strong>Install Spenderly</strong>
            <span>Home-screen app · offline shell · no app-store hunt</span>
          </div>
          <div className="pwa-install-actions">
            <button type="button" className="sp-btn-primary pwa-bar-btn" onClick={onInstallClick}>
              Install now
            </button>
            <button type="button" className="sp-btn-ghost pwa-bar-dismiss" onClick={closeInstall}>
              Not now
            </button>
          </div>
        </div>
      )}

      {showIosHelp && (
        <div className="sp-modal-overlay" role="presentation" onClick={() => setShowIosHelp(false)}>
          <div
            className="sp-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pwa-ios-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="pwa-ios-title" style={{ margin: '0 0 8px', fontSize: 20 }}>
              Add to Home Screen
            </h3>
            {isIosSafari() ? (
              <ol className="pwa-ios-steps">
                <li>Tap the <strong>Share</strong> button in Safari</li>
                <li>Scroll and tap <strong>Add to Home Screen</strong></li>
                <li>Tap <strong>Add</strong> — Spenderly opens like an app</li>
              </ol>
            ) : (
              <p className="sp-modal-message" style={{ marginBottom: 16 }}>
                In Chrome or Edge, open the browser menu and choose <strong>Install app</strong> or{' '}
                <strong>Add to Home screen</strong>. On iPhone, open this site in Safari first.
              </p>
            )}
            <div className="sp-modal-actions">
              <button type="button" className="sp-btn-primary" onClick={closeInstall}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
